import { useMemo } from "react";

import { Box, Divider, Grid } from "@mui/material";
import { AddressFields, ClientInsuranceNumberField, FormikTextField, toOptionItems } from "@shared";
import { ClientPersonalDataMaxLength } from "@shared";

import { ClientResponse } from "@/api";
import { FormikAutocomplete } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete";
import { FormikAutocompleteSelect } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocompleteSelect";
import { FormikDatePicker } from "@/common/components/fields/formik/formik-date-picker/FormikDatePicker";
import { FormDialog } from "@/common/components/form-dialog/FormDialog";
import { useTranslation } from "@/translations";

import { ClientDuplicateWarning } from "./components/client-duplicate-warning/ClientDuplicateWarning";
import { useClientCreation } from "./hooks/useClientCreation";

interface Props {
  onClose: () => void;
  onClientCreated?: (client: ClientResponse) => void;
}

export function ClientCreationDialog({ onClose, onClientCreated }: Props) {
  const { t } = useTranslation();
  const { formik, dropdownOptions, duplicates } = useClientCreation(onClose, onClientCreated);
  const employmentStatusOptions = useMemo(
    () => toOptionItems(dropdownOptions.employmentStatuses),
    [dropdownOptions.employmentStatuses]
  );

  const hasDuplicates = !!duplicates?.length;

  return (
    <FormDialog
      title={t("clients.create.dialogTitle")}
      onClose={onClose}
      formik={formik}
      submitLabel={hasDuplicates ? t("clients.create.createAnyway") : t("clients.create.submit")}
      submitColor={hasDuplicates ? "warning" : undefined}
    >
      <Box pt={2}>
        <Grid container rowSpacing={2} columnSpacing={2}>
          <Grid size={6}>
            <FormikDatePicker
              formik={formik}
              fieldName="initialRegistrationDate"
              label={t("clients.create.creationDate")}
              required
            />
          </Grid>
          <Grid size={6}>
            <FormikAutocomplete
              formik={formik}
              fieldName="contactSource"
              label={t("clients.create.contactSource")}
              options={dropdownOptions.contactSources}
            />
          </Grid>
          <Grid size={12}>
            <FormikTextField
              formik={formik}
              fieldName="comment"
              label={t("clients.create.comment")}
              multiline
              rows={3}
              slotProps={{ input: { inputProps: { maxLength: ClientPersonalDataMaxLength.comment } } }}
            />
          </Grid>

          <Grid size={12}>
            <Divider />
          </Grid>

          <Grid size={4}>
            <FormikAutocomplete
              formik={formik}
              fieldName="salutation"
              label={t("clients.create.salutation")}
              options={dropdownOptions.salutations}
            />
          </Grid>
          <Grid size={4}>
            <FormikAutocomplete
              formik={formik}
              fieldName="title"
              label={t("clients.create.title")}
              options={dropdownOptions.titles}
              freeSolo
            />
          </Grid>

          <Grid size={4} />

          <Grid size={4}>
            <FormikTextField
              formik={formik}
              fieldName="firstName"
              label={t("clients.create.name")}
              slotProps={{ input: { inputProps: { maxLength: ClientPersonalDataMaxLength.firstName } } }}
              required
            />
          </Grid>
          <Grid size={4}>
            <FormikTextField
              formik={formik}
              fieldName="lastName"
              label={t("clients.create.lastName")}
              slotProps={{ input: { inputProps: { maxLength: ClientPersonalDataMaxLength.lastName } } }}
              required
            />
          </Grid>
          <Grid size={4}>
            <FormikTextField
              formik={formik}
              fieldName="formerLastName"
              label={t("clients.create.formerLastName")}
              slotProps={{ input: { inputProps: { maxLength: ClientPersonalDataMaxLength.formerLastName } } }}
            />
          </Grid>

          <Grid size={12}>
            <Divider />
          </Grid>

          <AddressFields
            formik={formik}
            streetField="street"
            houseNumberField="houseNumber"
            zipCodeField="zipCode"
            cityField="city"
            countryField="country"
            streetMaxLength={ClientPersonalDataMaxLength.street}
            required={{ street: true, houseNumber: true, zipCode: true, city: true, country: true }}
          />
          <Grid size={6}>
            <FormikTextField
              formik={formik}
              fieldName="email"
              label={t("clients.create.email")}
              slotProps={{ input: { inputProps: { maxLength: ClientPersonalDataMaxLength.contactDetail.value } } }}
            />
          </Grid>
          <Grid size={6}>
            <FormikTextField
              formik={formik}
              fieldName="phone"
              label={t("clients.create.phone")}
              slotProps={{ input: { inputProps: { maxLength: ClientPersonalDataMaxLength.contactDetail.value } } }}
            />
          </Grid>

          <Grid size={12}>
            <Divider />
          </Grid>

          <Grid size={6}>
            <ClientInsuranceNumberField formik={formik} />
          </Grid>
          <Grid size={6}>
            <FormikDatePicker formik={formik} fieldName="birthday" label={t("clients.create.birthday")} />
          </Grid>

          <Grid size={12}>
            <Divider />
          </Grid>

          <Grid size={6}>
            <FormikAutocompleteSelect
              formik={formik}
              fieldName="employmentStatus"
              label={t("clients.create.employmentStatus")}
              options={employmentStatusOptions}
            />
          </Grid>

          {duplicates && duplicates.length > 0 && (
            <Grid size={12}>
              <ClientDuplicateWarning duplicates={duplicates} />
            </Grid>
          )}
        </Grid>
      </Box>
    </FormDialog>
  );
}
